import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import {
  arrayIsShallowEqual,
  Datastore,
  notEmpty,
} from '@freelancer/datastore';
import {
  PredictedSkillsCollection,
  Skill,
  SuggestedSkillsCollection,
} from '@freelancer/datastore/collections';
import { TimeUtils } from '@freelancer/time-utils';
import { HeadingType } from '@freelancer/ui/heading';
import { Margin } from '@freelancer/ui/margin';
import { SelectItem } from '@freelancer/ui/select';
import { SpinnerSize } from '@freelancer/ui/spinner';
import { TextSize } from '@freelancer/ui/text';
import { dirtyAndValidate } from '@freelancer/ui/validators';
import { isDefined } from '@freelancer/utils';
import * as Rx from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  flatMap,
  map,
  publishReplay,
  refCount,
  startWith,
  switchMapTo,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { JobType } from '../job-type/job-type.component';

@Component({
  selector: 'app-skills',
  template: `
    <fl-bit class="SpinnerContainer">
      <fl-heading
        i18n="Post Job Page form skills field label"
        [flMarginBottom]="Margin.XXXSMALL"
        [headingType]="HeadingType.H3"
        [size]="TextSize.MID"
      >
        What skills are required?
      </fl-heading>
      <fl-spinner
        class="SpinnerContainer-spinner"
        *ngIf="showLoadingSpinner$ | async"
        flTrackingLabel="PJPSkillsInitialisationSpinner"
        [size]="SpinnerSize.SMALL"
      ></fl-spinner>
    </fl-bit>
    <fl-bit [flMarginBottom]="Margin.SMALL">
      <fl-text
        *ngIf="(predictedSkills$ | async)?.length > 0; else noSkillsPredicted"
        i18n="Post Job Page form predicted skills field description"
      >
        <ng-container [ngSwitch]="jobType">
          <ng-container *ngSwitchCase="JobType.CONTEST">
            We've detected the following skills to suit your contest. Feel free
            to modify these choices to best suit your needs.
          </ng-container>
          <ng-container *ngSwitchDefault>
            We've detected the following skills to suit your project. Feel free
            to modify these choices to best suit your needs.
          </ng-container>
        </ng-container>
      </fl-text>
      <ng-template #noSkillsPredicted>
        <ng-container [ngSwitch]="jobType">
          <fl-text
            i18n="Post Job Page form skills field description"
            *ngSwitchCase="JobType.CONTEST"
          >
            Enter up to 5 skills that best describe your contest. Freelancers
            will use these skills to find contests they are most interested and
            experienced in.
          </fl-text>
          <fl-text
            i18n="Post Job Page form skills field description"
            *ngSwitchDefault
          >
            Enter up to 5 skills that best describe your project. Freelancers
            will use these skills to find projects they are most interested and
            experienced in.
          </fl-text>
        </ng-container>
      </ng-template>
    </fl-bit>
    <fl-bit [flMarginBottom]="Margin.XXXSMALL">
      <fl-multi-select
        flTrackingLabel="FocusIn"
        i18n-placeholder="Post Job Page form skills field placeholder"
        i18n-secondaryPlaceholder="Post Job Page form skills field placeholder"
        placeholder="Enter skills here..."
        secondaryPlaceholder="Enter skills here..."
        [control]="control"
        [options]="skillSelectItems$ | async"
      >
      </fl-multi-select>
    </fl-bit>
    <app-suggested-skills
      [skills]="suggestedSkills$ | async"
      (skillClick)="addSuggestedSkill($event)"
    >
    </app-suggested-skills>
  `,
  styleUrls: ['./skills.component.scss'],
})
export class SkillsComponent implements OnDestroy, OnInit {
  TextSize = TextSize;
  HeadingType = HeadingType;
  JobType = JobType;
  Margin = Margin;
  SpinnerSize = SpinnerSize;

  @Input() control: FormControl;
  @Input() description$: Rx.Observable<string>;
  @Input() jobType: JobType;
  @Input() skills$: Rx.Observable<ReadonlyArray<Skill>>;
  @Input() title$: Rx.Observable<string>;

  // Default skill id list from /project-create/skills-field/flSkillsField.js
  defaultSkillIds: ReadonlyArray<number> = [17, 32, 44, 39, 174];
  showLoadingSpinner$: Rx.Observable<boolean>;
  skillSelectItems$: Rx.Observable<ReadonlyArray<SelectItem>>;
  suggestedSkills$: Rx.Observable<ReadonlyArray<Skill>>;
  predictedSkills$: Rx.Observable<ReadonlyArray<Skill>>;

  selectedSkillsSubscription?: Rx.Subscription;

  constructor(private datastore: Datastore, private timeUtils: TimeUtils) {}

  ngOnInit() {
    this.skillSelectItems$ = this.skills$.pipe(
      map(skills =>
        skills.map((skill: Skill) => ({
          displayText: skill.name,
          value: skill,
        })),
      ),
    );

    this.selectedSkillsSubscription = this.skills$
      .pipe(
        notEmpty(),
        withLatestFrom(
          this.control.valueChanges.pipe(startWith(this.control.value)),
        ),
        tap(([skills, selectedSkills]) => {
          const freshSelectedSkills = selectedSkills
            .map((selectedSkill: Skill) =>
              skills.find(skill => skill.id === selectedSkill.id),
            )
            .filter(isDefined);

          this.control.setValue(freshSelectedSkills);
        }),
      )
      .subscribe();

    const selectedSkillIds$: Rx.Observable<ReadonlyArray<
      number
    >> = this.control.valueChanges.pipe(
      startWith(this.control.value),
      map((skills: ReadonlyArray<Skill>) => skills.map(skill => skill.id)),
    );

    const suggestedSkillsCollection = this.datastore.collection<
      SuggestedSkillsCollection
    >('suggestedSkills', query =>
      query.where(
        'skills',
        'equalsIgnoreOrder',
        selectedSkillIds$.pipe(this.timeUtils.rxDebounceTime(700), notEmpty()),
      ),
    );

    const suggestedSkillIds$ = suggestedSkillsCollection.valueChanges().pipe(
      flatMap(collection => collection.map(skill => skill.suggestedSkills)),
      startWith(this.defaultSkillIds),
    );

    const dedupedSuggestedSkillIds$ = Rx.combineLatest([
      suggestedSkillIds$,
      selectedSkillIds$,
    ]).pipe(
      map(([suggested, selected]) =>
        suggested.filter(id => !selected.includes(id)),
      ),
      publishReplay(1),
      refCount(),
    );

    this.suggestedSkills$ = Rx.combineLatest([
      dedupedSuggestedSkillIds$,
      this.skills$,
    ]).pipe(
      map(([suggested, skills]) =>
        suggested.map(id => this.getSkill(id, skills)),
      ),
      map(suggested => suggested.filter((x): x is Skill => !!x)),
      map(suggested => suggested.slice(0, 5)),
      publishReplay(1),
      refCount(),
    );

    const debouncedTitle$ = this.title$.pipe(
      this.timeUtils.rxDebounceTime(700),
      map(title => title.trim()),
      distinctUntilChanged(),
    );

    const debouncedDescription$ = this.description$.pipe(
      this.timeUtils.rxDebounceTime(700),
      map(description => description.trim()),
      distinctUntilChanged(),
    );

    const hasTitleAndDescription$ = Rx.combineLatest([
      debouncedTitle$,
      debouncedDescription$,
    ]).pipe(
      map(([title, description]) => title.length > 0 && description.length > 0),
      publishReplay(1),
      refCount(),
    );

    const predictedSkillsSubject$ = new Rx.BehaviorSubject<
      ReadonlyArray<Skill>
    >([]);

    const hasManuallySelectedSkills$ = this.control.valueChanges.pipe(
      startWith(this.control.value),
      distinctUntilChanged(),
      withLatestFrom(predictedSkillsSubject$),
      map(
        ([selected, predicted]) =>
          selected.length > 0 && !arrayIsShallowEqual(selected, predicted),
      ),
    );

    const isSkillPredictorEnabled$ = hasTitleAndDescription$.pipe(
      withLatestFrom(hasManuallySelectedSkills$),
      map(
        ([hasTitleAndDescription, hasManuallySelectedSkills]) =>
          hasTitleAndDescription && !hasManuallySelectedSkills,
      ),
      startWith(false),
    );

    const predictedSkillsCollection = this.datastore.collection<
      PredictedSkillsCollection
    >('predictedSkills', query =>
      Rx.combineLatest([
        isSkillPredictorEnabled$,
        debouncedTitle$,
        debouncedDescription$,
      ]).pipe(
        filter(
          ([_, title, description]) =>
            title.length > 0 && description.length > 0,
        ),
        map(([isEnabled, title, description]) =>
          isEnabled
            ? query
                .where('title', '==', title)
                .where('description', '==', description)
            : query.null(),
        ),
      ),
    );

    const predictedSkillsOnError$ = predictedSkillsCollection.status$.pipe(
      filter(status => !!status.error),
      switchMapTo(Rx.of([] as ReadonlyArray<number>)),
    );

    const predictedSkillsOnEmptyTitleOrDescription$ = hasTitleAndDescription$.pipe(
      withLatestFrom(hasManuallySelectedSkills$),
      filter(
        ([hasTitleAndDescription, hasManuallySelectedSkills]) =>
          !hasTitleAndDescription && !hasManuallySelectedSkills,
      ),
      switchMapTo(Rx.of([] as ReadonlyArray<number>)),
    );

    const predictedSkillIds$ = predictedSkillsCollection
      .valueChanges()
      .pipe(
        flatMap(predictions =>
          predictions.map(prediction => prediction.skills.slice(0, 5)),
        ),
      );

    this.predictedSkills$ = Rx.merge<readonly number[]>(
      predictedSkillsOnEmptyTitleOrDescription$,
      predictedSkillsOnError$,
      predictedSkillIds$,
    ).pipe(
      withLatestFrom(this.skills$, hasManuallySelectedSkills$),
      map(([ids, skills, hasManuallySelectedSkills]) =>
        hasManuallySelectedSkills
          ? []
          : ids.map(id => this.getSkill(id, skills)).filter(isDefined),
      ),
    );

    const showPredictedSkills$ = this.predictedSkills$.pipe(
      withLatestFrom(hasManuallySelectedSkills$),
      tap(([skills, hasManuallySelectedSkills]) => {
        predictedSkillsSubject$.next(skills);

        if (!hasManuallySelectedSkills && !this.control.touched) {
          this.control.setValue(skills);
        }
      }),
    );

    this.showLoadingSpinner$ = Rx.merge(
      isSkillPredictorEnabled$,
      showPredictedSkills$.pipe(map(() => false)),
    );
  }

  ngOnDestroy() {
    if (this.selectedSkillsSubscription) {
      this.selectedSkillsSubscription.unsubscribe();
    }
  }

  addSuggestedSkill(skill: Skill) {
    this.control.setValue([...this.control.value, skill]);
    dirtyAndValidate(this.control);
  }

  getSkill(id: number, skills: ReadonlyArray<Skill>): Skill | undefined {
    return skills ? skills.find(skill => skill.id === id) : undefined;
  }
}
